import React, { useState, useEffect, useMemo, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { Texture, SRGBColorSpace, WebGLRenderer } from "three";
import { useParams, useLocation } from "react-router-dom";
import { useStoreSelector, useStoreDispatch } from "../../../../../../hooks/StoreHooks";
import { useDoorConfigurationBuilderActions } from "../../../../../../store/reducers/door_configuration_builder";
import { useOverlapConfigurationsService } from "../../../../../services/overlap_configurations_service";
import { useFinishes } from "../../../../../providers/finish_provider";
import { DoorType, FinishType, InstallationType, ModelType } from "../../../../../core/common_types";
import { WindowSize, GridSize } from "../../../../../models/door_configuration";
import { coloreContrasto, getColoreManiglia, MM, PROFONDITA_GARAGE_DEFAULT } from "./elementi/constants";
import { coloreTrattoSuTexture } from "./elementi/contrasto_texture";
import { IntegratedDoorPosition } from "./elementi/constants";
import { Scene } from "./elementi/scene";
import rals from "../../../../../assets/rals.json";
import { getCurrentUser } from "../../../../../core/common_functions";

function invertHexColor(hex: string): string {
  const h = hex.replace('#', '');
  const r = (255 - parseInt(h.substring(0, 2), 16)).toString(16).padStart(2, '0');
  const g = (255 - parseInt(h.substring(2, 4), 16)).toString(16).padStart(2, '0');
  const b = (255 - parseInt(h.substring(4, 6), 16)).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

export type { OverbasStep } from "./elementi/constants";
export type { IntegratedDoorPosition } from "./elementi/constants";

const PortaOverbas: React.FC<{
  larghezza?: number;
  altezza?: number;
  spessore?: 40 | 75;
  profondita?: number;
  step?: string;
  tipoManiglia?: string | null;
  verniciaturaManiglia?: string | null;
  coloreManigliaRal?: string | null;
}> = ({ larghezza = 2.5, altezza = 2.3, spessore = 40, profondita = PROFONDITA_GARAGE_DEFAULT, step = "", tipoManiglia, verniciaturaManiglia, coloreManigliaRal }) => {
  const { id } = useParams();
  const location = useLocation();
  const isPreventivo = location.pathname.includes('/preventivo');
  const { base, doorConfigurationBuilder } = useStoreSelector(store => store);
  const finishesProvider = useFinishes();
  const [texturaPannello,               setTexturaPannello]               = useState<Texture | undefined>(undefined);
  const [texturaInternoPannello,         setTexturaInternoPannello]         = useState<Texture | undefined>(undefined);
  const [texturaPortina,                setTexturaPortina]                = useState<Texture | undefined>(undefined);
  const [texturaPortinaInterno,         setTexturaPortinaInterno]         = useState<Texture | undefined>(undefined);
  const [texturaPortinaLateraleCornice, setTexturaPortinaLateraleCornice] = useState<Texture | undefined>(undefined);
  const [texturaPortinaLateralePannello,setTexturaPortinaLateralePannello] = useState<Texture | undefined>(undefined);
  const [texturaCorniceFinestratura,    setTexturaCorniceFinestratura]    = useState<Texture | undefined>(undefined);

  const colorCode             = doorConfigurationBuilder.getExternalColorCode();
  const portinaIntegrata      = doorConfigurationBuilder.configuration?.portinaIntegrata;

  const portinaColorCode = portinaIntegrata?.externalFrameColorCode
    ?? (portinaIntegrata?.externalFrameColorType !== "ral_non_standard" ? colorCode : undefined);
  const portinaColorCodeInterno = portinaIntegrata?.internalFrameColorCode
    ?? (portinaIntegrata?.internalFrameColorType !== "ral_non_standard" ? colorCode : undefined);

  const portinaHandleColorCode = portinaIntegrata?.externalHandleColorCode;
  const portinaHandleColorType = portinaIntegrata?.externalHandleColorType;
  const portinaHandleColorCodeInterno = portinaIntegrata?.internalHandleColorCode;
  const portinaHandleColorTypeInterno = portinaIntegrata?.internalHandleColorType;

  const portinaApertura         = (portinaIntegrata?.apertura ?? "sx") as "sx" | "dx";
  const portinaSogliaAltezza    = (portinaIntegrata?.soglia?.rialzata ? portinaIntegrata?.soglia?.altezza ?? 0 : 0) as number;
  const portinaLateraleApertura = (doorConfigurationBuilder.configuration?.portinaAffiancata?.versoApertura ?? "sx") as "sx" | "dx";

  const portinaManigliaCOlore = useMemo(() => {
    if (portinaHandleColorType === "ral_non_standard" && portinaHandleColorCode) {
      const ral = rals.find(r => r.RAL === portinaHandleColorCode);
      return ral ? ral.HEX : "#000000";
    }
    return "#000000";
  }, [portinaHandleColorType, portinaHandleColorCode]);

  const portinaManigliaCOloreInterno = useMemo(() => {
    if (portinaHandleColorTypeInterno === "ral_non_standard" && portinaHandleColorCodeInterno) {
      const ral = rals.find(r => r.RAL === portinaHandleColorCodeInterno);
      return ral ? ral.HEX : "#000000";
    }
    return "#000000";
  }, [portinaHandleColorTypeInterno, portinaHandleColorCodeInterno]);

  // Le finiture a texture (woodstyle, HPL, pietravera) non hanno un RAL corrispondente:
  // il tratto segue allora la luminanza media del campione, altrimenti sui legni scuri
  // il grigio di ripiego si confondeva con la venatura e le sezioni sparivano.
  const hoverColor = useMemo(() => {
    const ralEntry = rals.find(r => r.RAL === colorCode);
    return ralEntry ? invertHexColor(ralEntry.HEX) : coloreTrattoSuTexture(texturaPannello);
  }, [colorCode, texturaPannello]);

  // Bordi dei tubolari: colore che stacca rispetto alla porta.
  const coloreBordoTubolare = useMemo(
    () => coloreContrasto(rals.find(r => r.RAL === colorCode)?.HEX),
    [colorCode],
  );

  const telaioPerimetraleRal = doorConfigurationBuilder.getTelaioPerimetraleColorCode();
  const coloreTelaioPerimetrale = useMemo(
    () => rals.find(r => r.RAL === telaioPerimetraleRal)?.HEX,
    [telaioPerimetraleRal],
  );

  console.log("[PortaOverbas] colori", {
    colorCode,
    portinaColorCode,
    portinaLateraleCorniceColorCode: doorConfigurationBuilder.configuration?.portinaAffiancata?.coloreEsterno,
    rawConfiguration: doorConfigurationBuilder.configuration,
  });

  const internalColorCode = doorConfigurationBuilder.getInternalColorCode();

  useEffect(() => {
    finishesProvider?.createTexture(colorCode).then(txt => {
      if (txt) txt.colorSpace = SRGBColorSpace;
      setTexturaPannello(txt);
    });
  }, [colorCode]);

  useEffect(() => {
    finishesProvider?.createTexture(internalColorCode).then(txt => {
      if (txt) txt.colorSpace = SRGBColorSpace;
      setTexturaInternoPannello(txt);
    });
  }, [internalColorCode]);

  useEffect(() => {
    finishesProvider?.createTexture(portinaColorCode).then(txt => {
      if (txt) txt.colorSpace = SRGBColorSpace;
      setTexturaPortina(txt);
    });
  }, [portinaColorCode]);

  useEffect(() => {
    finishesProvider?.createTexture(portinaColorCodeInterno).then(txt => {
      if (txt) txt.colorSpace = SRGBColorSpace;
      setTexturaPortinaInterno(txt);
    });
  }, [portinaColorCodeInterno]);

  larghezza  = MM(doorConfigurationBuilder.configuration?.larghezzaPorta ?? 2300);
  altezza    = MM(doorConfigurationBuilder.configuration?.altezzaPorta ?? 2300);
  profondita = MM(doorConfigurationBuilder.configuration?.profonditaGarage ?? 6000);
  step       = doorConfigurationBuilder.steps.find((s: any) => s.isActive)?.slug ?? '';

  // Foro = luce muraria; porta = pannello ordinato. Se il foro non e' valorizzato
  // si ricade sulle misure porta (il foro coincide con la porta).
  const larghezzaForo = MM(doorConfigurationBuilder.configuration?.larghezzaForo ?? 0) || larghezza;
  const altezzaForo   = MM(doorConfigurationBuilder.configuration?.altezzaForo ?? 0)   || altezza;

  const oltreLuceSx = doorConfigurationBuilder.configuration?.tipoInstallazioneMontanteSx === InstallationType.oltre_luce;
  const oltreLuceDx = doorConfigurationBuilder.configuration?.tipoInstallazioneMontanteDx === InstallationType.oltre_luce;
  const oltreLuceTraversa = doorConfigurationBuilder.configuration?.tipoInstallazioneTraversa === InstallationType.oltre_luce;

  // Spalletta/architrave: misure reali rilevate in cantiere. La spalletta e'
  // letteralmente il muro che resta fra il foro e la parete laterale: a 0 la
  // porta risulta appoggiata direttamente al muro laterale (lo squarcio arriva
  // fino a li'). L'architrave non scende mai sotto i 30 mm, lo stesso minimo
  // gia' richiesto in fase di validazione per l'oltre luce.
  const larghezzaSpallettaSx = MM(doorConfigurationBuilder.configuration?.larghezzaSpallettaSx ?? 0);
  const larghezzaSpallettaDx = MM(doorConfigurationBuilder.configuration?.larghezzaSpallettaDx ?? 0);
  const altezzaArchitrave    = Math.max(MM(doorConfigurationBuilder.configuration?.altezzaArchitrave ?? 0), MM(30));

  const effectiveTipoManiglia = tipoManiglia !== undefined
    ? tipoManiglia
    : doorConfigurationBuilder.configuration?.maniglieria?.tipoManiglia ?? null;

  // Sul passo "maniglieria" la verniciatura arriva dalle props (anteprima live),
  // altrove si ricade su quanto gia' salvato nella configurazione
  const maniglieriaSalvata = doorConfigurationBuilder.configuration?.maniglieria;
  const effectiveVerniciaturaManiglia = verniciaturaManiglia !== undefined
    ? verniciaturaManiglia
    : doorConfigurationBuilder.getVerniciaturaManigliaNome() ?? null;
  const effectiveRalManiglia = coloreManigliaRal !== undefined
    ? coloreManigliaRal
    : maniglieriaSalvata?.verniciaturaRal ?? null;

  const coloreManiglia = useMemo(
    () => getColoreManiglia(effectiveVerniciaturaManiglia, effectiveRalManiglia),
    [effectiveVerniciaturaManiglia, effectiveRalManiglia]
  );

  // La porta resta chiusa dove l'apertura darebbe fastidio: su finestratura e ventilazione
  // le aperture si posizionano cliccando sui pannelli, sul preventivo si scatta l'anteprima
  const canOpenDoor = !isPreventivo && step !== "finestratura" && step !== "ventilazione";

  const isDebordante = doorConfigurationBuilder.configuration?.isDebordante ?? true;

  const vanoConAltroAccesso = doorConfigurationBuilder.configuration?.vanoConAltroAccesso ?? false;
  const isWide = doorConfigurationBuilder.options
    .find((o: any) => o?.optionId === doorConfigurationBuilder.configuration?.modello)
    ?.name === ModelType.wide;

  const isFullVision = doorConfigurationBuilder.options
    .find((o: any) => o?.optionId === doorConfigurationBuilder.configuration?.porta)
    ?.name === DoorType.full_vision;

  const finituraName = doorConfigurationBuilder.options
    .find((o: any) => o?.optionId === doorConfigurationBuilder.configuration?.finitura)
    ?.name;

  const portaName = doorConfigurationBuilder.options
    .find((o: any) => o?.optionId === doorConfigurationBuilder.configuration?.porta)
    ?.name;

  const isPlana = portaName === DoorType.plana;

  const isPietravera       = isPlana && finituraName === FinishType.pietravera;
  const isGlasspanelFinish = isPlana && finituraName === FinishType.glasspanel;
  
  const isTwoPanels =
    isPlana &&
    (isGlasspanelFinish ||
     finituraName === FinishType.lamiera_8_10 ||
     finituraName === FinishType.material_hpl ||
     finituraName === FinishType.wood_hpl ||
     isPietravera);

  const rawL = doorConfigurationBuilder.configuration?.larghezzaPorta ?? 0;
  const rawH = doorConfigurationBuilder.configuration?.altezzaPorta ?? 0;

  const pietraveraColumns: number = (() => {
    if (isPietravera) {
      if (rawL <= 2720) {
        if (rawH <= 2580) return 1;
        return 2;
      }
      if (rawL <= 3920) {
        if (rawH <= 2580) return 2;
        return 3;
      }
      if (rawL <= 5120) {
        if (rawH <= 2580) return 2;
        return 4;
      }
      if (rawL <= 5970) {
        if (rawH <= 2580) return 4;
        return 5;
      }
      return 4;
    }
    if (isGlasspanelFinish) {
      if (rawL <= 3270) return 1;
      if (rawL <= 3770) return 2;
      return 3;
    }
    return 1;
  })();

  const dispatcher = useStoreDispatch();
  const { setFinestratura, setVentilazione } = useDoorConfigurationBuilderActions();
  const configurationService = useOverlapConfigurationsService();
  const [isOpen, setIsOpen] = useState(false);
  const currentUser = getCurrentUser(base);
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);
  const [photoSuccess, setPhotoSuccess] = useState(false);
  const glRef = useRef<WebGLRenderer | null>(null);

  const takeCurrentViewPhoto = async () => {
    setIsPhotoLoading(true);
    setPhotoSuccess(false);
    const gl = glRef.current;
    if (!gl) { setIsPhotoLoading(false); return; }
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = gl.domElement.width;
    tempCanvas.height = gl.domElement.height;
    const ctx = tempCanvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    ctx.drawImage(gl.domElement, 0, 0);
    tempCanvas.toBlob(async (blob) => {
      const configId = doorConfigurationBuilder.configuration?.configurationId;
      if (blob && configId) {
        const file = new File([blob], "preview.jpg", { type: "image/jpeg" });
        try {
          await configurationService.savePreview(currentUser, configId, file);
          setPhotoSuccess(true);
          setTimeout(() => setPhotoSuccess(false), 2000);
        } catch (e) {
          console.error("Errore upload preview:", e);
        }
      }
      setIsPhotoLoading(false);
    }, "image/jpeg", 0.95);
  };

  const showPortina       = !!doorConfigurationBuilder.configuration?.portinaIntegrata?.portina;
  const portinaPosizione  = (doorConfigurationBuilder.configuration?.portinaIntegrata?.posizione ?? "c") as IntegratedDoorPosition;
  const rawLarghezza      = doorConfigurationBuilder.configuration?.portinaIntegrata?.larghezza;
  const portinaLarghezza  = MM(typeof rawLarghezza === "number" ? rawLarghezza : rawLarghezza?.esternoTelaio ?? 800);

  const showPortinaLaterale             = !!doorConfigurationBuilder.configuration?.portinaAffiancata?.portina;
  const portinaLateraleInstallazione    = (doorConfigurationBuilder.configuration?.portinaAffiancata?.tipoInstallazione ?? "in_luce") as InstallationType;
  const portinaLateraleCorniceColorCode  = doorConfigurationBuilder.configuration?.portinaAffiancata?.externalFrameColorCode;
  const portinaLateralePannelloColorCode = doorConfigurationBuilder.configuration?.portinaAffiancata?.coloreEsterno;

  const portinaLateraleHandleColorType = doorConfigurationBuilder.configuration?.portinaAffiancata?.externalHandleColorType;
  const portinaLateraleHandleColorCode = doorConfigurationBuilder.configuration?.portinaAffiancata?.externalHandleColorCode;
  const portinaLateraleHandleColor = useMemo(() => {
    if (portinaLateraleHandleColorType === "ral_non_standard" && portinaLateraleHandleColorCode) {
      const ral = rals.find(r => r.RAL === portinaLateraleHandleColorCode);
      return ral ? ral.HEX : "#111111";
    }
    return "#111111";
  }, [portinaLateraleHandleColorType, portinaLateraleHandleColorCode]);

  useEffect(() => {
    finishesProvider?.createTexture(portinaLateraleCorniceColorCode).then(txt => {
      if (txt) txt.colorSpace = SRGBColorSpace;
      setTexturaPortinaLateraleCornice(txt);
    });
  }, [portinaLateraleCorniceColorCode]);

  useEffect(() => {
    finishesProvider?.createTexture(portinaLateralePannelloColorCode).then(txt => {
      if (txt) txt.colorSpace = SRGBColorSpace;
      setTexturaPortinaLateralePannello(txt);
    });
  }, [portinaLateralePannelloColorCode]);

  const finestraturaCorniceColorCode = doorConfigurationBuilder.configuration?.finestratura?.externalFrameColorCode;
  useEffect(() => {
    finishesProvider?.createTexture(finestraturaCorniceColorCode).then(txt => {
      if (txt) txt.colorSpace = SRGBColorSpace;
      setTexturaCorniceFinestratura(txt);
    });
  }, [finestraturaCorniceColorCode]);

  const _paf        = doorConfigurationBuilder.configuration?.portinaAffiancata;
  const _portaName  = _paf?.usaStessaFinitura
    ? doorConfigurationBuilder.options.find((o: any) => o?.optionId === doorConfigurationBuilder.configuration?.porta)?.name
    : _paf?.porta?.name;
  const _modelloName = _paf?.usaStessaFinitura
    ? doorConfigurationBuilder.options.find((o: any) => o?.optionId === doorConfigurationBuilder.configuration?.modello)?.name
    : _paf?.modello?.name;
  const portinaLateralePanelCount: number =
    _portaName === DoorType.classic
      ? (_modelloName === ModelType.wide ? 8 : 4)
      : 1;

  const finestratura = doorConfigurationBuilder.configuration?.finestratura;
  const ventilazione = doorConfigurationBuilder.configuration?.ventilazione;
  const windowSize: WindowSize = isGlasspanelFinish
    ? WindowSize.glasspanel
    : (finestratura?.windowSize as WindowSize) ?? WindowSize.undefined;
  const gridSize: GridSize = (ventilazione?.gridSize as GridSize) ?? GridSize.undefined;

  const grigliaColor = useMemo(() => {
    const code = ventilazione?.externalColorCode;
    if (!code) return undefined;
    const ral = rals.find(r => r.RAL === code);
    return ral ? ral.HEX : undefined;
  }, [ventilazione?.externalColorCode]);

  const finestreAttive = useMemo<Set<string>>(() => {
    if (isGlasspanelFinish) {
      // Tutti i pannelli attivi automaticamente per finitura glasspanel
      const panelCount = Math.round(rawH / 500);
      const set = new Set<string>();
      for (let i = 0; i < panelCount; i++) set.add(`${i}-0`);
      return set;
    }
    const set = new Set<string>();
    finestratura?.positioning?.forEach((p: any) => {
      set.add(`${p.panelNumber - 1}-${p.position}`);
    });
    return set;
  }, [isGlasspanelFinish, rawH, finestratura?.positioning]);

  const griglieAttive = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    ventilazione?.positioning?.forEach((p: any) => {
      set.add(`${p.panelNumber - 1}-${p.position}`);
    });
    return set;
  }, [ventilazione?.positioning]);

  const handleFinestraClick = (panelIndex: number, finestraIndex: number) => {
    if (step === "ventilazione") {
      if (!ventilazione) return;
      const positioning = (ventilazione.positioning ?? []).map((p: any) => ({ ...p }));
      const existing = positioning.findIndex((p: any) => p.panelNumber === panelIndex + 1 && p.position === finestraIndex);
      if (existing >= 0) {
        positioning.splice(existing, 1);
      } else {
        positioning.push({ panelNumber: panelIndex + 1, position: finestraIndex, elementsNumber: 1, bbox: { x: 0, y: 0, width: 0, height: 0 } });
      }
      dispatcher(setVentilazione({ ...ventilazione, positioning }));
    } else {
      if (!finestratura) return;
      const positioning = (finestratura.positioning ?? []).map((p: any) => ({ ...p }));
      const existing = positioning.findIndex((p: any) => p.panelNumber === panelIndex + 1 && p.position === finestraIndex);
      if (existing >= 0) {
        positioning.splice(existing, 1);
      } else {
        positioning.push({ panelNumber: panelIndex + 1, position: finestraIndex, elementsNumber: 1, bbox: { x: 0, y: 0, width: 0, height: 0 } });
      }
      dispatcher(setFinestratura({ ...finestratura, positioning }));
    }
  };

  const cameraDistance = Math.max(larghezzaForo, altezzaForo, larghezza, altezza) * 2.8;

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <Canvas
        gl={{ preserveDrawingBuffer: true }}
        camera={{
          position: [larghezzaForo / 2 + 1, altezzaForo / 2 + 0.5, cameraDistance],
          fov: 45,
        }}
        onCreated={({ gl }) => { glRef.current = gl as WebGLRenderer; }}
      >
        <Scene
          larghezza={larghezza}
          altezza={altezza}
          spessore={MM(spessore)}
          isOpen={canOpenDoor && isOpen}
          showPortina={showPortina}
          portinaPosizione={portinaPosizione}
          portinaLarghezza={portinaLarghezza}
          portinaColore={texturaPortina}
          portinaColoreInterno={texturaPortinaInterno}
          portinaManigliaCOlore={portinaManigliaCOlore}
          portinaManigliaCOloreInterno={portinaManigliaCOloreInterno}
          portinaSogliaAltezza={portinaSogliaAltezza}
          portinaApertura={portinaApertura}
          portinaLateraleApertura={portinaLateraleApertura}
          showPortinaLaterale={showPortinaLaterale}
          portinaLateraleInstallazione={portinaLateraleInstallazione}
          portinaLateraleCorniceColorCode={texturaPortinaLateraleCornice}
          portinaLateralePannelloColorCode={texturaPortinaLateralePannello}
          portinaLateraleHandleColor={portinaLateraleHandleColor}
          portinaLateralePanelCount={portinaLateralePanelCount}
          vanoConAltroAccesso={vanoConAltroAccesso}
          step={step}
          profondita={profondita}
          larghezzaForo={larghezzaForo}
          altezzaForo={altezzaForo}
          oltreLuceSx={oltreLuceSx}
          oltreLuceDx={oltreLuceDx}
          oltreLuceTraversa={oltreLuceTraversa}
          larghezzaSpallettaSx={larghezzaSpallettaSx}
          larghezzaSpallettaDx={larghezzaSpallettaDx}
          altezzaArchitrave={altezzaArchitrave}
          finestreAttive={finestreAttive}
          griglieAttive={griglieAttive}
          onFinestraClick={handleFinestraClick}
          colorePannello={texturaPannello}
          coloreInternoPannello={texturaInternoPannello ?? texturaPannello}
          corniceFinestratura={texturaCorniceFinestratura}
          isWide={isWide}
          isFullVision={isFullVision}
          isTwoPanels={isTwoPanels}
          pietraveraColumns={pietraveraColumns}
          windowSize={windowSize}
          hoverColor={hoverColor}
          gridSize={gridSize}
          grigliaColor={grigliaColor}
          tipoManiglia={effectiveTipoManiglia}
          coloreManiglia={coloreManiglia}
          rawAltezza={rawH}
          isDebordante={isDebordante}
          coloreBordoTubolare={coloreBordoTubolare}
          coloreTelaioPerimetrale={coloreTelaioPerimetrale}
        />
      </Canvas>

      {/* Pulsante Apri/Chiudi */}
      {canOpenDoor && (
        <button
          onClick={() => setIsOpen((v) => !v)}
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            padding: "12px 24px",
            fontSize: "16px",
            fontWeight: "600",
            backgroundColor: isOpen ? "#d32f2f" : "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            zIndex: 100,
          }}
        >
          {isOpen ? "Chiudi la basculante" : "Apri la basculante"}
        </button>
      )}
      {isPreventivo && (
        <button
          onClick={() => takeCurrentViewPhoto()}
          disabled={isPhotoLoading}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            padding: '8px 14px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: photoSuccess ? '#15803d' : isPhotoLoading ? '#374151' : '#1e3a8a',
            color: '#fff',
            cursor: isPhotoLoading ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'background-color 0.3s',
            minWidth: '120px',
            zIndex: 100,
          }}
          title="Scatta foto dalla posizione attuale"
        >
          {isPhotoLoading ? '⏳ Invio...' : photoSuccess ? '✅ Salvata!' : '📷 Scatta Anteprima'}
        </button>
      )}
    </div>
  );
};

export default PortaOverbas;
